import React from 'react';
import { useMemberHistory } from '../hooks/useWelfareAssistance.ts';

const money = (v) => 'KES ' + Number(v || 0).toLocaleString();
const STATUS = { pending_vetting: 'waiting', approved: 'approved', rejected: 'rejected', completed: 'completed' };

/* The facts an official needs before approving a welfare request: what the member still owes to other welfare
   collections, what they have paid, and what they asked for before. Shown inside a request and in the new-request form. */
export default function MemberWelfareRecord({ memberId, requestId, compact = false }) {
  const { data, isLoading } = useMemberHistory(memberId, true, requestId);
  if (isLoading) return <p className="text-xs text-gray-500">Loading record...</p>;
  if (!data) return null;

  const { eligibility: e, owing, paid, requests, received } = data;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-600 p-3 text-sm space-y-2" data-testid="member-welfare-record">
      <p className={`font-medium ${e.qualified ? 'text-green-700' : 'text-red-700'}`}>
        {e.qualified
          ? `Good standing: owes for ${e.unpaid_debts_count} ${e.unpaid_debts_count === 1 ? 'person' : 'people'} (approval is needed at ${data.debt_limit} or more).`
          : `Owes for ${e.unpaid_debts_count} people (${data.debt_limit} or more needs approval).`}
      </p>

      {owing.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide">Still owes</p>
          <ul className="mt-1 space-y-0.5 text-gray-700 dark:text-slate-200">
            {owing.map((o) => (
              <li key={o.plan_id}>{o.plan_name}{o.for ? ` (for ${o.for})` : ''}{o.since ? ` · since ${o.since}` : ''}</li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-1">A collection counts once it is closed, or open for more than {data.grace_days} days. A member who declined to join, or the person it was for, is not counted.</p>
        </div>
      )}

      <p className="text-gray-700 dark:text-slate-200">Has paid into {paid.plans} collection{paid.plans === 1 ? '' : 's'}, {money(paid.total)} in total.</p>

      {!compact && received.length > 0 && (
        <p className="text-gray-700 dark:text-slate-200">Was helped before: {received.map((r) => `${r.plan_name} (${money(r.raised)} raised)`).join(', ')}.</p>
      )}

      {requests.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide">Earlier requests</p>
          <ul className="mt-1 space-y-0.5 text-gray-700 dark:text-slate-200">
            {requests.map((r) => (
              <li key={r.id}>{r.date} · {r.title} · {money(r.amount)} · {STATUS[r.status] || r.status}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
